import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { shopAPI } from '../api/shop';
import { inventoryAPI } from '../api/inventory';
import { toast } from '../components/common';
import type { PurchaseItemRequest } from '../types/shop';

// Shop Catalog Queries
export const useShopCatalog = () => {
  return useQuery({
    queryKey: ['shop', 'catalog'],
    queryFn: () => shopAPI.getCatalog(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useShopItems = () => {
  return useQuery({
    queryKey: ['shop', 'items'],
    queryFn: () => shopAPI.getItems(),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

export const useFeaturedItems = () => {
  return useQuery({
    queryKey: ['shop', 'featured'],
    queryFn: () => shopAPI.getFeaturedItems(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export const useShopCategories = () => {
  return useQuery({
    queryKey: ['shop', 'categories'],
    queryFn: () => shopAPI.getCategories(),
    staleTime: 15 * 60 * 1000, // 15 minutes
  });
};

// Currency Queries
export const useCurrencyBalances = () => {
  return useQuery({
    queryKey: ['currency', 'balances'],
    queryFn: () => shopAPI.getCurrencyBalances(),
    staleTime: 30 * 1000, // 30 seconds
  });
};

// Inventory Queries
export const useInventory = () => {
  return useQuery({
    queryKey: ['inventory'],
    queryFn: () => inventoryAPI.getInventory(),
    staleTime: 60 * 1000, // 1 minute
  });
};

export const useInventoryStats = () => {
  return useQuery({
    queryKey: ['inventory', 'stats'],
    queryFn: () => inventoryAPI.getStats(),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// Transaction Queries
export const useTransactions = (params?: { page?: number; limit?: number; type?: string }) => {
  return useQuery({
    queryKey: ['transactions', params],
    queryFn: () => shopAPI.getTransactions(params),
    staleTime: 60 * 1000, // 1 minute
  });
};

// Purchase Mutations
export const usePurchaseItem = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: PurchaseItemRequest) => shopAPI.purchaseItem(data),
    onSuccess: (response) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['currency', 'balances'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      
      toast.success(`Successfully purchased ${response.data.item?.name || 'item'}!`);
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Purchase failed';
      toast.error(message);
    },
  });
};

export const useUseItem = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ itemId, quantity = 1, metadata }: { itemId: number; quantity?: number; metadata?: any }) => 
      inventoryAPI.useItem({ itemId, quantity, metadata }),
    onSuccess: (response) => {
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      
      toast.success(`Used ${response.data.quantityUsed} item(s)!`);
    },
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to use item';
      toast.error(message);
    },
  });
};